import type { BucketGranularity } from './bucketSeries'

/**
 * X-axis tick selection shared by the time-axis chart primitives (TimeSeriesChart,
 * ComposedChart, ColumnChart). Handing recharts an explicit, evenly-spaced tick set
 * keeps every label under its data point and stops the axis over-crowding — left to
 * its own default (`interval="preserveEnd"`) thinning, recharts squishes the labels
 * toward the end of the range. Pure, no React.
 */

/** Default number of x-axis ticks once a range is too long to label every day. */
export const DEFAULT_X_TICK_COUNT = 6
/** At/below this many days, label every day; above it, thin to DEFAULT_X_TICK_COUNT
 *  (labelling every day would drop one and leave an uneven gap). */
export const DAILY_TICK_LIMIT = 8
/** Above this many buckets a one-label-per-bucket axis would crowd, so a week/month
 *  view thins instead. Sized above the counts real ranges produce (3-month weekly
 *  ≈ 14, 1-year monthly ≈ 13) so those label every bucket. */
export const MAX_BUCKET_LABELS = 16

/**
 * Picks `count` evenly-spaced items from `values` by index (always including the
 * first and last), preserving order. Returns the input unchanged when it already
 * has `count` or fewer items.
 *
 * Used to thin axis ticks to a legible number while keeping every tick aligned to
 * a real data position — so a tick label sits exactly under its data point rather
 * than at a recharts auto-generated position between days.
 */
export function subsampleEvenly<T>(values: T[], count: number): T[] {
  if (count <= 0 || values.length === 0) return []
  if (values.length <= count) return values
  if (count === 1) return [values[0]]
  const step = (values.length - 1) / (count - 1)
  const picks: T[] = []
  for (let i = 0; i < count; i++) picks.push(values[Math.round(i * step)])
  return picks
}

/**
 * Day-aligned default x-axis ticks (a subset of the real data timestamps) so each
 * label sits under its data point and the axis never over-crowds. Every day for
 * short ranges; thinned to `tickCount` (default {@link DEFAULT_X_TICK_COUNT}) beyond
 * {@link DAILY_TICK_LIMIT}. Returns `undefined` for ranges too short to place ticks
 * (recharts then picks its own single/degenerate tick).
 *
 * TimeSeriesChart (a numeric axis) always did this; the category-axis charts used to
 * pass no ticks and let recharts thin them, which squished the tail of long ranges.
 */
export function defaultDayTicks(
  timestamps: number[],
  tickCount?: number,
): number[] | undefined {
  if (timestamps.length <= 1) return undefined
  if (tickCount == null && timestamps.length <= DAILY_TICK_LIMIT) {
    return timestamps
  }
  return subsampleEvenly(timestamps, tickCount ?? DEFAULT_X_TICK_COUNT)
}

/**
 * Ticks for a bucketed time axis (the bar/category charts). Week/month views label
 * **every bucket** — one label per bar, so gaps are uniform and both end dates show;
 * on a category axis a tick can only sit on a bucket, so labelling every one is the
 * only stride that stays even for any count. A caller tick set (e.g. the
 * data-collection flag's day-aligned days) is ignored on bucketed views — those days
 * miss the week/month buckets on a category axis and vanish — so bucketed always
 * labels its own buckets. Daily views thin to a legible set (see {@link defaultDayTicks}),
 * where a comparison-aligned set (so both dates stack) then a caller set take
 * precedence. Runaway bucket counts (very long monthly ranges) fall back to an even
 * subsample rather than crowd.
 */
export function chartXAxisTicks(
  granularity: BucketGranularity,
  timestamps: number[],
  opts: {
    callerTicks?: number[]
    comparisonTicks?: number[]
    tickCount?: number
  } = {},
): number[] | undefined {
  const { callerTicks, comparisonTicks, tickCount } = opts
  if (timestamps.length <= 1) return timestamps
  if (granularity !== 'day') {
    return timestamps.length <= MAX_BUCKET_LABELS
      ? timestamps
      : subsampleEvenly(timestamps, tickCount ?? DEFAULT_X_TICK_COUNT)
  }
  return (
    comparisonTicks ?? callerTicks ?? defaultDayTicks(timestamps, tickCount)
  )
}
